import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Image as AntImage, Skeleton } from "antd";

import Image from "components/Image";
import { getResignationDetailApi } from "api/services/resignations";
import ResignationStatusBadge from "./ResignationStatusBadge";
import ResignationTimeline from "./ResignationTimeline";
import ResignationActions from "./ResignationActions";
import {
  formatDay,
  fullName,
  isOverdue,
  noticeDays,
  noticeLabel,
  relativeDay,
} from "./resignationFormat";
import "../resignation.css";

const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "heic"];

const fileName = (url = "") => {
  const last = url.split("?")[0].split("/").pop() || "Attachment";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

const extension = (url = "") => fileName(url).split(".").pop().toLowerCase();

/** Attachment URLs are stored as submitted (process_attachments does not
 *  validate them), so a `javascript:` URL would run in the approver's session
 *  on click. Only http(s) is ever rendered as a link or image. */
export const safeUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

const AttachmentItem = ({ file }) => {
  const url = safeUrl(file.url);
  if (!url) return <span className="resignation-files__doc text-muted">Unsupported link</span>;
  if (IMAGE_EXT.includes(extension(url))) {
    return <AntImage src={url} alt={fileName(url)} width={72} height={72} style={{ objectFit: "cover", borderRadius: 8 }} />;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="resignation-files__doc">
      <i className={extension(url) === "pdf" ? "fa-solid fa-file-pdf" : "fa-solid fa-file"} aria-hidden="true" />
      <span>{fileName(url)}</span>
    </a>
  );
};

const Fact = ({ label, value, hint, tone }) => (
  <div className="resignation-fact">
    <div className="resignation-fact__label">{label}</div>
    <div className="resignation-fact__value">{value}</div>
    {hint && <div className={`resignation-fact__hint ${tone ? `is-${tone}` : ""}`}>{hint}</div>}
  </div>
);

const Section = ({ title, children, aside, className = "" }) => (
  <section className={`resignation-section ${className}`}>
    <div className="d-flex align-items-baseline justify-content-between">
      <h4 className="resignation-section__title">{title}</h4>
      {aside}
    </div>
    {children}
  </section>
);

const Header = ({ resignation }) => {
  const person = resignation.company_user || {};
  const subtitle = [person.emp_no, person.position_name].filter(Boolean).join(" · ");
  return (
    <div className="resignation-header">
      <Image src={person.profile_image} type="user" alt="" className="resignation-header__avatar" style={{ width: 56, height: 56 }} />
      <div className="resignation-header__who">
        {person.id ? (
          <Link to={`/employees/${person.id}`} className="resignation-header__name">{fullName(person)}</Link>
        ) : (
          <span className="resignation-header__name">{fullName(person)}</span>
        )}
        <div className="resignation-header__sub">{subtitle || "-"}</div>
        {person.email && <div className="resignation-header__sub">{person.email}</div>}
      </div>
      <ResignationStatusBadge status={resignation.approval_status} />
    </div>
  );
};

const Facts = ({ resignation }) => {
  const overdue = isOverdue(resignation);
  const awaiting = resignation.approval_status === "pending" ? fullName(resignation.current_approver) : "-";
  return (
    <div className="resignation-facts">
      <Fact
        label="Last working day"
        value={formatDay(resignation.last_working_day)}
        hint={overdue ? `Passed ${relativeDay(resignation.last_working_day)}` : relativeDay(resignation.last_working_day)}
        tone={overdue ? "danger" : undefined}
      />
      <Fact label="Applied on" value={formatDay(resignation.created_at)} hint={relativeDay(resignation.created_at)} />
      <Fact label="Notice given" value={noticeLabel(noticeDays(resignation)) || "-"} />
      <Fact label="Awaiting" value={awaiting} />
    </div>
  );
};

const Attachments = ({ attachments }) => {
  if (!attachments?.length) return <div className="text-muted small">No attachments.</div>;
  return (
    <AntImage.PreviewGroup>
      <ul className="resignation-files">
        {attachments.map((file) => (
          <li key={file.id || file.url}>
            <AttachmentItem file={file} />
          </li>
        ))}
      </ul>
    </AntImage.PreviewGroup>
  );
};

/** Loads one resignation and renders it as a read-first document: who, the
 *  dates that matter, the reason in full, where it sits in the approval chain,
 *  then — only for the person who can act — the decision form.
 *
 *  `layout="split"` (full page) puts the letter beside the facts and decision
 *  on wide screens; the drawer keeps the single column.
 *
 *  `summary` is the list row, rendered immediately so the drawer never opens
 *  blank; the detail fetch then fills in approvals and attachments. */
const ResignationDetailPanel = ({ id, summary, onChanged, layout = "stack" }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!id) return undefined;
    let ignore = false;
    setError("");
    getResignationDetailApi(id)
      .then((response) => { if (!ignore) setDetail(response.data); })
      .catch(() => { if (!ignore) setError("Could not load this resignation."); });
    return () => { ignore = true; };
  }, [id, reloadToken]);

  const resignation = { ...summary, ...detail };
  if (!resignation.id) {
    return error ? <div className="alert alert-danger">{error}</div> : <Skeleton active avatar paragraph={{ rows: 6 }} />;
  }

  const handleDone = () => {
    setReloadToken((t) => t + 1);
    onChanged?.();
  };

  // Two wrappers so the full page can lay out letter | decision side by side.
  // In the drawer (and on narrow screens) they are `display: contents` and the
  // sections fall back into one column, ordered by CSS.
  return (
    <div className={`resignation-detail ${layout === "split" ? "is-split" : ""}`}>
      <Header resignation={resignation} />
      <div className="resignation-detail__main">
        <Section title="Reason" className="is-reason">
          <p className="resignation-reason">{resignation.reason || "No reason given."}</p>
        </Section>
      </div>
      <div className="resignation-detail__aside">
        {isOverdue(resignation) && (
          <div className="resignation-alert" role="status">
            <i className="fa fa-exclamation-triangle" aria-hidden="true" /> The last working day has passed and this request is still pending.
          </div>
        )}
        <Facts resignation={resignation} />
        {detail && <ResignationActions resignation={resignation} onDone={handleDone} />}
        <Section title="Approval history" className="is-timeline">
          {detail ? <ResignationTimeline resignation={resignation} /> : <Skeleton active paragraph={{ rows: 3 }} title={false} />}
        </Section>
        <Section title="Attachments" className="is-files">
          {detail ? <Attachments attachments={resignation.attachments} /> : <Skeleton active paragraph={{ rows: 1 }} title={false} />}
        </Section>
        {error && <div className="alert alert-danger">{error}</div>}
      </div>
    </div>
  );
};

export default ResignationDetailPanel;
